import { useEffect, useState } from 'react';
import { MEDIA_EVENTS } from './constants';
export type MediaInfoT = {
  width: number | null;
  height: number | null;
  duration: number | null;
  fileSize: number | null;
  format: string | null;
};
type PropsT = {
  mediaElement: HTMLVideoElement | HTMLAudioElement | null;
  fetchFileSizeAndFormat?: (src: string) => Promise<{
    fileSize: number | null;
    format: string | null;
  }>;
};
const getMediaDuration = (mediaElement: HTMLMediaElement | null) => {
  if (!mediaElement) {
    return null;
  }
  if (mediaElement.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    return null;
  }
  if (isNaN(mediaElement.duration)) {
    return null;
  }
  return mediaElement.duration;
};
export function useMediaInfo({
  mediaElement,
  fetchFileSizeAndFormat
}: PropsT) {
  const [mediaInfo, setMediaInfo] = useState<MediaInfoT>({
    width: mediaElement instanceof HTMLVideoElement ? mediaElement.videoWidth || null : null,
    height: mediaElement instanceof HTMLVideoElement ? mediaElement.videoHeight : null,
    duration: getMediaDuration(mediaElement),
    fileSize: null,
    format: null
  });
  useEffect(() => {
    if (!mediaElement) return;
    const handleLoadedMetadata = async () => {
      let fileSizeFormatInfo: Pick<MediaInfoT, 'fileSize' | 'format'> | null = null;
      if (fetchFileSizeAndFormat) {
        fileSizeFormatInfo = await fetchFileSizeAndFormat(mediaElement.currentSrc);
      }
      setMediaInfo(prev => ({
        ...prev,
        width: mediaElement instanceof HTMLVideoElement ? mediaElement.videoWidth || null : null,
        height: mediaElement instanceof HTMLVideoElement ? mediaElement.videoHeight || null : null,
        duration: getMediaDuration(mediaElement),
        ...(fileSizeFormatInfo || {})
      }));
    };
    if (mediaElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      handleLoadedMetadata();
    }
    mediaElement.addEventListener(MEDIA_EVENTS.LOADED_METADATA, handleLoadedMetadata);
    return () => {
      mediaElement.removeEventListener(MEDIA_EVENTS.LOADED_METADATA, handleLoadedMetadata);
    };
  }, [fetchFileSizeAndFormat, mediaElement]);
  return mediaInfo;
}